import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import ProductCard from "@/components/ProductCard";
import { fileUrl } from "@/components/UploadButton";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const { content } = useContent();
  const hero = content.hero || {};
  const cats = content.categories || {};
  const ha = content.home_about || {};
  const fh = content.featured || {};

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products?special=true").then((r) => setSpecials(r.data)).catch(() => {});
    api.get("/products/new-releases?days=6").then((r) => setNewReleases(r.data)).catch(() => {});
  }, []);

  // Union of specials + featured, specials shown first, deduped by id.
  const workshopPicks = (() => {
    const seen = new Set();
    const merged = [];
    for (const p of [...specials, ...featured]) {
      if (!seen.has(p.id)) { seen.add(p.id); merged.push(p); }
    }
    return merged;
  })();

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[88vh] grain overflow-hidden flex items-end">
        {hero.image && (
          <img
            src={fileUrl(hero.image)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#E0F2F1] via-[#E0F2F1]/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-32 w-full">
          {hero.kicker && <p className="smallcaps mb-8 fade-up">{hero.kicker}</p>}
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter text-[#212121] max-w-3xl leading-[0.95] fade-up" style={{ animationDelay: "0.1s" }}>
            {hero.headline_pre}
            {hero.headline_highlight && (
              <em className="text-[#B87333] not-italic font-black">{hero.headline_highlight}</em>
            )}
            {hero.headline_post}
          </h1>
          {hero.subtext && (
            <p className="text-[#757575] max-w-xl mt-8 leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
              {hero.subtext}
            </p>
          )}
          <div className="flex flex-wrap gap-4 mt-10 fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary">
              {hero.cta_primary || "Browse the Shop"} <ArrowRight size={14} />
            </Link>
            <Link to="/custom-order" data-testid="hero-custom-btn" className="btn-secondary">
              {hero.cta_secondary || "Commission a Piece"}
            </Link>
          </div>
        </div>
      </section>

      {/* New releases — auto-expiring spotlight (last 6 days). Hidden when empty. */}
      {newReleases.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 sm:pt-24"
          data-testid="new-releases-section"
        >
          <div className="flex items-end justify-between mb-10 sm:mb-12">
            <div>
              <p className="smallcaps mb-3" data-testid="new-releases-kicker">Fresh from the workshop</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[#212121]" data-testid="new-releases-heading">New releases</h2>
            </div>
            <Link
              to="/shop"
              className="hidden sm:flex items-center gap-2 text-[#757575] hover:text-[#B87333] font-bold transition-colors text-sm tracking-wider"
              data-testid="new-releases-shop-link"
            >
              Shop all
              <ArrowRight size={16} />
            </Link>
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8"
            data-testid="new-releases-grid"
          >
            {newReleases.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        <Link to="/category/chopping-boards" data-testid="cat-chopping" className="group relative aspect-[4/3] overflow-hidden border border-[#B2DFDB]">
          {cats.chopping_image && <img src={fileUrl(cats.chopping_image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#E0F2F1] via-[#E0F2F1]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <p className="smallcaps mb-2">Category</p>
            <p className="font-display text-3xl text-[#212121]">Chopping Boards</p>
          </div>
        </Link>
        <Link to="/category/resin-platters" data-testid="cat-resin" className="group relative aspect-[4/3] overflow-hidden border border-[#B2DFDB]">
          {cats.resin_image && <img src={fileUrl(cats.resin_image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#E0F2F1] via-[#E0F2F1]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <p className="smallcaps mb-2">Category</p>
            <p className="font-display text-3xl text-[#212121]">Resin Platters</p>
          </div>
        </Link>
        <Link to="/category/furniture" data-testid="cat-furniture" className="group relative aspect-[4/3] overflow-hidden border border-[#B2DFDB]">
          {cats.furniture_image && <img src={fileUrl(cats.furniture_image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#E0F2F1] via-[#E0F2F1]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <p className="smallcaps mb-2">Category</p>
            <p className="font-display text-3xl text-[#212121]">Furniture</p>
          </div>
        </Link>
        <Link to="/category/miscellaneous" data-testid="cat-miscellaneous" className="group relative aspect-[4/3] overflow-hidden border border-[#B2DFDB]">
          {cats.miscellaneous_image && <img src={fileUrl(cats.miscellaneous_image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#E0F2F1] via-[#E0F2F1]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <p className="smallcaps mb-2">Category</p>
            <p className="font-display text-3xl text-[#212121]">Miscellaneous</p>
          </div>
        </Link>
      </section>

      {/* Featured + Specials */}
      {workshopPicks.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 sm:pb-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              {fh.kicker && <p className="smallcaps mb-3">{fh.kicker}</p>}
              {fh.heading && <h2 className="font-display text-4xl sm:text-5xl text-[#212121]">{fh.heading}</h2>}
            </div>
            <Link to="/shop" className="text-[#757575] hover:text-[#B87333] font-bold text-sm uppercase tracking-widest hidden sm:inline">
              View all →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {workshopPicks.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Artisan */}
      <section className="border-t border-[#B2DFDB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32 grid md:grid-cols-2 gap-16 items-center">
          {ha.image && <img src={fileUrl(ha.image)} alt="" className="aspect-[4/5] object-cover border border-[#B2DFDB]" />}
          <div>
            {ha.kicker && <p className="smallcaps mb-4">{ha.kicker}</p>}
            {ha.heading && (
              <h2 className="font-display text-4xl sm:text-5xl text-[#212121] mb-6 leading-tight">{ha.heading}</h2>
            )}
            {ha.paragraph1 && <p className="text-[#757575] leading-relaxed mb-4">{ha.paragraph1}</p>}
            {ha.paragraph2 && <p className="text-[#757575] leading-relaxed mb-8">{ha.paragraph2}</p>}
            <Link to="/about" className="btn-secondary">Read the Story</Link>
          </div>
        </div>
      </section>

      {/* Workshops banner */}
      {content.workshops && (
        <section className="border-t border-[#B2DFDB] bg-[#0E0E0E] grain">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32 grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              {content.workshops.kicker && <p className="smallcaps mb-4 text-[#B87333] font-bold">{content.workshops.kicker}</p>}
              {content.workshops.heading && (
                <h2 className="font-display text-4xl sm:text-5xl text-[#212121] mb-6 leading-tight">{content.workshops.heading}</h2>
              )}
              {content.workshops.subtext && (
                <p className="text-[#757575] leading-relaxed mb-8 max-w-xl">{content.workshops.subtext}</p>
              )}
              <Link to="/workshops" data-testid="home-workshops-cta" className="btn-primary">
                Join the waitlist
              </Link>
            </div>
            {content.workshops.image && (
              <img src={fileUrl(content.workshops.image)} alt="" className="md:col-span-2 aspect-[4/5] object-cover border border-[#B2DFDB]" />
            )}
          </div>
        </section>
      )}
    </div>
  );
}
